import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
    Alert,
    FlatList,
    Image,
    Platform,
    RefreshControl,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import { RectButton, Swipeable } from 'react-native-gesture-handler';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { AuthContext } from '../context/AuthContext';
import { ScoreCard } from '../components/ui/ScoreCard';
import Colors from '../constants/Colors';
import api from '../services/api';
import { confirmAction, showAlert } from '../services/confirm';

const theme = Colors.light;
const { spacing, radius } = Colors;

export default function HomeScreen() {
    const { user, refreshPartner } = useContext(AuthContext);
    const [history, setHistory] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [pairCodeInput, setPairCodeInput] = useState('');
    const [submittingPairRequest, setSubmittingPairRequest] = useState(false);
    const [pairRequestHint, setPairRequestHint] = useState('');

    const handlingClearRequestRef = useRef(false);
    const promptedClearRequestIdRef = useRef(null);

    const loadData = useCallback(async () => {
        try {
            const res = await api.get('/score/history');
            setHistory(Array.isArray(res?.data) ? res.data : []);
        } catch (error) {
            // Keep previous UI state if background refresh fails.
        } finally {
            setRefreshing(false);
        }
    }, []);

    const loadPairRequestHint = useCallback(async () => {
        if (user?.partnerId) {
            setPairRequestHint('');
            return;
        }

        try {
            const response = await api.get('/user/pair/status');
            const request = response?.data?.request;
            if (!request || request.requestType !== 'pair') {
                setPairRequestHint('');
                return;
            }

            const counterpartName = request.counterpartName || '对方';
            if (request.isResponder) {
                setPairRequestHint(`${counterpartName} 向你发起了配对请求，请到“消息”页处理。`);
            } else {
                setPairRequestHint(`已向 ${counterpartName} 发起配对请求，等待对方确认。`);
            }
        } catch (error) {
            setPairRequestHint('');
        }
    }, [user?.partnerId]);

    const handlePairByCode = useCallback(async () => {
        const normalizedCode = pairCodeInput.trim();

        if (!normalizedCode) {
            showAlert('提示', '请输入配对码。');
            return;
        }
        if (!/^\d{6}$/.test(normalizedCode)) {
            showAlert('提示', '配对码必须是 6 位数字。');
            return;
        }

        try {
            setSubmittingPairRequest(true);
            const response = await api.post('/user/pair/request', { partnerCode: normalizedCode });
            setPairCodeInput('');
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            showAlert('已发送', response?.data?.message || '配对请求已发送。');
            await loadPairRequestHint();
        } catch (error) {
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
            showAlert('发送失败', error?.response?.data?.message || '发送配对请求失败。');
        } finally {
            setSubmittingPairRequest(false);
        }
    }, [loadPairRequestHint, pairCodeInput]);

    const handleClearResponse = useCallback(async (requestId, action) => {
        try {
            const response = await api.post('/score/clear/respond', { requestId, action });
            await loadData();
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            showAlert('已处理', response?.data?.message || '请求已处理。');
        } catch (error) {
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
            showAlert('处理失败', error?.response?.data?.message || '处理请求失败。');
        } finally {
            handlingClearRequestRef.current = false;
            promptedClearRequestIdRef.current = null;
        }
    }, [loadData]);

    const pollClearRequest = useCallback(async () => {
        if (!user?.id || !user?.partnerId || handlingClearRequestRef.current) return;

        try {
            const response = await api.get('/score/clear/status');
            const request = response?.data?.request;
            if (!request) {
                promptedClearRequestIdRef.current = null;
                return;
            }

            const requestId = Number(request?.id || 0);
            const responderId = Number(request?.partner_id || 0);
            if (!requestId || responderId !== Number(user.id)) return;
            if (promptedClearRequestIdRef.current === requestId) return;

            promptedClearRequestIdRef.current = requestId;
            handlingClearRequestRef.current = true;
            const promptMessage = '你的伴侣请求清空全部评分记录，是否同意？';

            if (Platform.OS === 'web') {
                const approved = window.confirm(`${promptMessage}\n\n确认执行此操作？`);
                await handleClearResponse(requestId, approved ? 'confirm' : 'reject');
                return;
            }

            Alert.alert(
                '清空评分记录',
                promptMessage,
                [
                    { text: '拒绝', style: 'cancel', onPress: () => { void handleClearResponse(requestId, 'reject'); } },
                    { text: '同意', style: 'destructive', onPress: () => { void handleClearResponse(requestId, 'confirm'); } },
                ],
                { cancelable: false }
            );
        } catch (error) {
            handlingClearRequestRef.current = false;
            promptedClearRequestIdRef.current = null;
        }
    }, [handleClearResponse, user?.id, user?.partnerId]);

    const handleClearRequest = () => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
        confirmAction(
            '清空评分记录',
            '向伴侣发送清空记录请求？该操作需要对方确认。',
            async () => {
                try {
                    const res = await api.post('/score/clear/request');
                    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
                    showAlert('已发送', res?.data?.message || '请求已发送。');
                } catch (error) {
                    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
                    showAlert('发送失败', error?.response?.data?.message || '请求发送失败。');
                }
            },
            '发送'
        );
    };

    const handleUndo = (item) => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
        confirmAction(
            '撤销评分',
            `确认撤销这条记录：“${item.reason}”？`,
            async () => {
                try {
                    await api.post(`/score/undo/${item.id}`);
                    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
                    await loadData();
                } catch (error) {
                    showAlert('撤销失败', error?.response?.data?.message || '无法撤销该记录。');
                }
            },
            '撤销'
        );
    };

    useFocusEffect(
        useCallback(() => {
            loadData();
            refreshPartner();
            pollClearRequest();
            loadPairRequestHint();

            const scoreInterval = setInterval(loadData, 5000);
            const clearRequestInterval = setInterval(pollClearRequest, 2500);
            const pairRequestInterval = setInterval(loadPairRequestHint, 3000);

            return () => {
                clearInterval(scoreInterval);
                clearInterval(clearRequestInterval);
                clearInterval(pairRequestInterval);
            };
        }, [loadData, loadPairRequestHint, pollClearRequest, refreshPartner])
    );

    const myId = user?.id;
    const partnerId = user?.partnerId;

    const myScores = history.filter((score) => score.receiverId === myId);
    const partnerScores = history.filter((score) => score.receiverId === partnerId);
    const myTotal = myScores.reduce((acc, item) => acc + item.amount, 0);
    const partnerTotal = partnerScores.reduce((acc, item) => acc + item.amount, 0);

    const myName = user?.nickname || user?.username || '我';
    const partnerName = user?.partnerNickname || user?.partnerUsername || (user?.partnerId ? '伴侣' : '等待配对');
    const relationshipLabel = user?.relationshipStatus === 'married' ? '已婚' : '恋爱中';

    const sortedHistory = useMemo(() => {
        return [...history].sort((a, b) => {
            const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [history]);

    const renderItem = ({ item }) => {
        const isMe = item.receiverId === myId;
        const receiverName = item.receiver?.nickname || item.receiver?.username || (isMe ? myName : partnerName);

        const isMyGiving = item.giverId === myId;
        const createdAtMs = item?.createdAt ? new Date(item.createdAt).getTime() : 0;
        const canUndo = isMyGiving && createdAtMs > 0 && (Date.now() - createdAtMs < 24 * 60 * 60 * 1000);

        let swipeableRow = null;

        return (
            <Swipeable
                enabled={canUndo}
                overshootRight={false}
                ref={(ref) => { swipeableRow = ref; }}
                renderRightActions={() => (
                    canUndo ? (
                        <View style={styles.swipeActions}>
                            <RectButton
                                style={styles.swipeDeleteBtn}
                                onPress={() => {
                                    swipeableRow?.close?.();
                                    handleUndo(item);
                                }}
                            >
                                <Ionicons name="trash-outline" size={18} color="#fff" />
                                <Text style={styles.swipeDeleteText}>删除</Text>
                            </RectButton>
                        </View>
                    ) : null
                )}
            >
                <ScoreCard
                    title={`${receiverName}: ${item.reason}`}
                    score={item.amount}
                    date={item.createdAt ? new Date(item.createdAt).toLocaleString('zh-CN', { hour12: false }) : '刚刚'}
                    type={item.amount > 0 ? 'gain' : 'loss'}
                    style={{ width: '100%' }}
                />
            </Swipeable>
        );
    };

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <View style={styles.titleBar}>
                <Text style={styles.largeTitle}>评分</Text>
            </View>

            <View style={styles.headerSection}>
                <View style={styles.scoreboard}>
                    <View style={styles.scoreCol}>
                        <View>
                            {user?.avatar ? (
                                <Image source={{ uri: user.avatar }} style={styles.avatarImage} />
                            ) : (
                                <View style={[styles.avatarCircle, { backgroundColor: theme.primaryLight }]}>
                                    <Text style={styles.avatarInitial}>{myName.charAt(0)}</Text>
                                </View>
                            )}
                            {myTotal > partnerTotal && (
                                <View style={styles.crownContainer}>
                                    <Text style={styles.crownText}>👑</Text>
                                </View>
                            )}
                        </View>
                        <Text style={styles.scoreName} numberOfLines={1}>{myName}</Text>
                        <Text style={[styles.scoreNum, { color: theme.primary }]}>{myTotal}</Text>
                    </View>

                    <View style={styles.vsCenter}>
                        <Text style={[styles.vsLabel, { fontSize: 10, marginBottom: 4, color: theme.primary }]}>
                            {relationshipLabel}
                        </Text>
                        <Text style={styles.vsLabel}>对比</Text>
                    </View>

                    <View style={styles.scoreCol}>
                        <View>
                            {user?.partnerAvatar ? (
                                <Image source={{ uri: user.partnerAvatar }} style={styles.avatarImage} />
                            ) : (
                                <View style={[styles.avatarCircle, { backgroundColor: theme.secondaryLight }]}>
                                    <Text style={styles.avatarInitial}>{partnerName.charAt(0)}</Text>
                                </View>
                            )}
                            {partnerTotal > myTotal && (
                                <View style={styles.crownContainer}>
                                    <Text style={styles.crownText}>👑</Text>
                                </View>
                            )}
                        </View>
                        <Text style={styles.scoreName} numberOfLines={1}>{partnerName}</Text>
                        <Text style={[styles.scoreNum, { color: theme.secondary }]}>{partnerTotal}</Text>
                    </View>
                </View>

                {!user?.partnerId && (
                    <View style={styles.pairingCard}>
                        <Text style={styles.pairingLabel}>我的配对码</Text>
                        <Text style={styles.pairingCode} selectable>{user?.pairingCode}</Text>
                        <Text style={styles.pairingHint}>把这个配对码分享给伴侣，即可完成账号绑定。</Text>

                        <View style={styles.pairRequestRow}>
                            <TextInput
                                style={styles.pairCodeInput}
                                value={pairCodeInput}
                                onChangeText={setPairCodeInput}
                                placeholder="输入伴侣配对码"
                                keyboardType="numeric"
                                maxLength={6}
                            />
                            <TouchableOpacity
                                style={[styles.pairRequestBtn, submittingPairRequest && styles.pairRequestBtnDisabled]}
                                onPress={handlePairByCode}
                                disabled={submittingPairRequest}
                            >
                                <Text style={styles.pairRequestBtnText}>
                                    {submittingPairRequest ? '发送中' : '请求配对'}
                                </Text>
                            </TouchableOpacity>
                        </View>

                        {Boolean(pairRequestHint) && (
                            <Text style={styles.pairRequestHintText}>{pairRequestHint}</Text>
                        )}
                    </View>
                )}
            </View>

            <View style={styles.actionBar}>
                <Text style={styles.sectionTitle}>操作</Text>
                <View style={styles.actionRow}>
                    {user?.partnerId && (
                        <TouchableOpacity onPress={handleClearRequest} style={styles.textBtn}>
                            <Text style={styles.textBtnLabel}>清空评分</Text>
                        </TouchableOpacity>
                    )}
                </View>
            </View>

            {history.length === 0 ? (
                <View style={styles.emptyState}>
                    <Text style={styles.emptyIcon}>📝</Text>
                    <Text style={styles.emptyTitle}>暂无记录</Text>
                    <Text style={styles.emptySubtitle}>去新增第一条评分吧。</Text>
                </View>
            ) : (
                <FlatList
                    data={sortedHistory}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={renderItem}
                    refreshControl={(
                        <RefreshControl refreshing={refreshing} onRefresh={loadData} tintColor={theme.primary} />
                    )}
                    contentContainerStyle={{ paddingHorizontal: spacing.xl, paddingBottom: spacing.xxxl }}
                    showsVerticalScrollIndicator={false}
                />
            )}
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.background,
    },
    titleBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: spacing.xl,
        paddingTop: spacing.md,
        paddingBottom: spacing.sm,
    },
    largeTitle: {
        fontSize: 34,
        fontWeight: '700',
        color: theme.text,
        letterSpacing: 0.4,
    },
    headerSection: {
        paddingHorizontal: spacing.xl,
        marginBottom: spacing.sm,
    },
    scoreboard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.card,
        borderRadius: radius.card,
        paddingVertical: spacing.xl,
        paddingHorizontal: spacing.lg,
        ...theme.shadow,
    },
    scoreCol: {
        flex: 1,
        alignItems: 'center',
    },
    avatarCircle: {
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: spacing.sm,
    },
    avatarImage: {
        width: 48,
        height: 48,
        borderRadius: 24,
        marginBottom: spacing.sm,
        backgroundColor: theme.border,
    },
    avatarInitial: {
        fontSize: 20,
        fontWeight: '600',
        color: theme.text,
    },
    scoreName: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.text,
        marginBottom: spacing.xs,
    },
    scoreNum: {
        fontSize: 32,
        fontWeight: '700',
        letterSpacing: -1,
    },
    vsCenter: {
        width: 40,
        alignItems: 'center',
    },
    vsLabel: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.muted,
        letterSpacing: 1,
    },
    pairingCard: {
        marginTop: spacing.md,
        backgroundColor: theme.card,
        borderRadius: radius.card,
        padding: spacing.xl,
        alignItems: 'center',
        ...theme.shadow,
    },
    pairingLabel: {
        fontSize: 13,
        color: theme.textSecondary,
        fontWeight: '500',
    },
    pairingCode: {
        fontSize: 32,
        fontWeight: '700',
        color: theme.text,
        marginVertical: spacing.md,
        letterSpacing: 6,
    },
    pairingHint: {
        fontSize: 12,
        color: theme.textSecondary,
    },
    pairRequestRow: {
        marginTop: spacing.md,
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    pairCodeInput: {
        flex: 1,
        height: 40,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: theme.border,
        backgroundColor: theme.backgroundSecondary,
        paddingHorizontal: 12,
        color: theme.text,
    },
    pairRequestBtn: {
        height: 40,
        borderRadius: radius.md,
        paddingHorizontal: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.blue,
    },
    pairRequestBtnDisabled: {
        opacity: 0.6,
    },
    pairRequestBtnText: {
        color: '#fff',
        fontSize: 13,
        fontWeight: '600',
    },
    pairRequestHintText: {
        marginTop: spacing.sm,
        width: '100%',
        color: theme.textSecondary,
        fontSize: 12,
        lineHeight: 18,
    },
    actionBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: spacing.xl,
        paddingVertical: spacing.md,
    },
    sectionTitle: {
        fontSize: 20,
        fontWeight: '600',
        color: theme.text,
        letterSpacing: -0.3,
    },
    actionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.lg,
    },
    textBtn: {
        paddingVertical: spacing.sm,
    },
    textBtnLabel: {
        fontSize: 15,
        color: theme.blue,
        fontWeight: '400',
    },
    emptyState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingBottom: 80,
    },
    emptyIcon: {
        fontSize: 44,
        marginBottom: spacing.lg,
    },
    emptyTitle: {
        fontSize: 17,
        fontWeight: '600',
        color: theme.text,
    },
    emptySubtitle: {
        fontSize: 14,
        color: theme.textSecondary,
        marginTop: spacing.xs,
    },
    crownContainer: {
        position: 'absolute',
        top: -12,
        right: -8,
        transform: [{ rotate: '15deg' }],
        zIndex: 10,
    },
    crownText: {
        fontSize: 24,
    },
    swipeActions: {
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: 3,
        marginLeft: spacing.md,
    },
    swipeDeleteBtn: {
        backgroundColor: theme.destructive,
        borderRadius: radius.card,
        paddingHorizontal: spacing.lg,
        height: 56,
        minWidth: 92,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: 6,
    },
    swipeDeleteText: {
        color: '#fff',
        fontSize: 13,
        fontWeight: '700',
        letterSpacing: -0.2,
    },
});
